import { useEffect, useMemo } from "react";
import { CalendarToday, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Grid } from "@mui/material";
import CustomText from "../components/CustomText";

// Mapa compatible con acentos y variaciones
const dayMap = {
  domingo: 0,
  lunes: 1,
  martes: 2,
  miercoles: 3,
  miércoles: 3,
  jueves: 4,
  viernes: 5,
  sabado: 6,
  sábado: 6,
};

const dayNames = ["DOM", "LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB"];

const monthNames = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const HoursDoc = ({
  doctor,
  selectedDate,
  setSelectedDate,
  weekOffset,
  setWeekOffset,
}) => {
  // Días laborales del médico
  const workingDays = useMemo(() => {
    if (!doctor?.horarios?.length) return [];

    const uniqueDays = [...new Set(doctor.horarios.map((h) => h.dia))];

    return uniqueDays
      .map((dia) => dayMap[dia?.toLowerCase()?.trim()])
      .filter((d) => d !== undefined);
  }, [doctor]);

  // Próximas fechas disponibles
  const availableDays = useMemo(() => {
    if (workingDays.length === 0) return [];

    const today = new Date();

    // Punto de inicio (semana actual + offset)
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + weekOffset * 7);

    const result = [];
    const cursor = new Date(startDate);

    // Obtener 7 fechas disponibles
    while (result.length < 7) {
      if (workingDays.includes(cursor.getDay())) {
        const isToday = cursor.toDateString() === today.toDateString();

        result.push({
          date: new Date(cursor),
          label: isToday ? "HOY" : dayNames[cursor.getDay()],
          day: cursor.getDate(),
          month: monthNames[cursor.getMonth()],
        });
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    return result;
  }, [weekOffset, workingDays]);

  // Seleccionar automáticamente el primer día disponible
  useEffect(() => {
    if (
      availableDays.length > 0 &&
      !availableDays.some(
        (d) => d.date.toDateString() === selectedDate.toDateString(),
      )
    ) {
      setSelectedDate(availableDays[0].date);
    }
  }, [availableDays, selectedDate, setSelectedDate]);

  // Si no tiene horarios
  if (workingDays.length === 0) {
    return (
      <Grid size={{ xs: 12 }}>
        <Box sx={design.ScheduleWrapper}>
          <CustomText
            color="#6B7280"
            fontFamily="Plus Jakarta Sans"
            fontSize={14}
          >
            Este médico aún no tiene horarios disponibles.
          </CustomText>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12 }}>
      <Box sx={design.ScheduleWrapper}>
        {/* Header */}
        <Box sx={design.ScheduleHeader}>
          <Box sx={design.ScheduleIcon}>
            <CalendarToday fontSize="small" />
          </Box>

          <Box sx={{ flex: 1 }}>
            <CustomText
              variant="body1"
              color="#123055"
              textAlign="left"
              fontFamily="Plus Jakarta Sans"
              fontWeight={800}
              fontSize={15}
            >
              Elige tu horario
            </CustomText>

            <CustomText
              variant="body2"
              color="#6B7280"
              textAlign="left"
              fontFamily="Plus Jakarta Sans"
              fontSize={12}
            >
              Disponibilidad de los próximos días
            </CustomText>
          </Box>

          {/* Flechas */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              onClick={() => setWeekOffset((prev) => Math.max(prev - 1, 0))}
              sx={design.ArrowButton}
            >
              <ChevronLeft fontSize="small" />
            </Box>

            <Box
              onClick={() => setWeekOffset((prev) => prev + 1)}
              sx={design.ArrowButton}
            >
              <ChevronRight fontSize="small" />
            </Box>
          </Box>
        </Box>

        {/* Días disponibles */}
        <Box sx={design.DaysRow}>
          {availableDays.map((item) => {
            const active =
              selectedDate.toDateString() === item.date.toDateString();

            return (
              <Box
                key={item.date.toISOString()}
                onClick={() => setSelectedDate(item.date)}
                sx={{
                  ...design.DayCard,
                  ...(active ? design.DayCardActive : {}),
                }}
              >
                <CustomText
                  variant="caption"
                  color={active ? "#FFFFFF" : "#5B6472"}
                  fontFamily="Plus Jakarta Sans"
                  fontWeight={700}
                  fontSize={11}
                >
                  {item.label}
                </CustomText>

                <CustomText
                  variant="h5"
                  color={active ? "#FFFFFF" : "#123055"}
                  fontFamily="Plus Jakarta Sans"
                  fontWeight={900}
                  fontSize={18}
                >
                  {item.day}
                </CustomText>

                <CustomText
                  variant="caption"
                  color={active ? "#D7E6FF" : "#7B8595"}
                  fontFamily="Plus Jakarta Sans"
                  fontWeight={600}
                  fontSize={11}
                >
                  {item.month}
                </CustomText>
              </Box>
            );
          })}
        </Box>

        {/* Fecha seleccionada */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <CustomText
            color="#123055"
            fontFamily="Plus Jakarta Sans"
            fontWeight={700}
            fontSize={13}
          >
            Fecha seleccionada:{" "}
            {selectedDate.toLocaleDateString("es-MX", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </CustomText>
        </Box>
      </Box>
    </Grid>
  );
};

export default HoursDoc;

const design = {
  ScheduleWrapper: {
    mt: 3,
    mb: 1,
    p: 2,
    borderRadius: "24px",
    border: "1px solid #D8E2EE",
    background: "#F8FAFC",
  },

  ScheduleHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 2,
  },

  ScheduleIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "#D2EFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  DaysRow: {
    display: "flex",
    gap: 1,
    overflowX: "auto",
    pb: 0.5,
    pt: 0.5,
    justifyContent: "center",
    "&::-webkit-scrollbar": {
      height: 6,
    },

    "&::-webkit-scrollbar-thumb": {
      background: "#C7D2E2",
      borderRadius: 999,
    },
  },

  DayCard: {
    minWidth: 68,
    height: 92,
    borderRadius: "22px",
    border: "1px solid #CFD8E3",
    background: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.25,
    cursor: "pointer",
    transition: "all .22s ease",

    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px rgba(18,48,85,.10)",
    },
  },

  DayCardActive: {
    background: "#102B52",
    borderColor: "#102B52",
    boxShadow: "0 12px 24px rgba(16,43,82,.22)",
  },

  ArrowButton: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "1px solid #D8E2EE",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: ".2s",

    "&:hover": {
      background: "#F3F7FB",
      transform: "scale(1.05)",
    },
  },
};
